import { useState, useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useSavedJobs } from "@/hooks/useSavedJobs";
import { supabase } from "@/lib/supabase";
import AnimatedModal from "@/components/base/AnimatedModal";

// Helper: generate company slug from company name
function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[đĐ]/g, "d")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

interface Job {
  id: string;
  title: string;
  company: string;
  location: string;
  salary: string;
  type: string;
  isHot: boolean;
  description: string;
  detail: string;
  requirements: string[];
  benefits: string[];
  postedAt: string;
  expiryDate: string;
  postedBy: string;
  employerEmail: string | null;
  employerPhone: string | null;
}

interface JobCardProps {
  job: Job;
}

export default function JobCard({ job }: JobCardProps) {
  const { user, isAuthenticated } = useAuth();
  const { isSaved, toggleSave, fetchSavedJobs } = useSavedJobs();
  const [expanded, setExpanded] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [showBookmarkLoginPrompt, setShowBookmarkLoginPrompt] = useState(false);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [showBannedAlert, setShowBannedAlert] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [applyState, setApplyState] = useState<"confirm" | "no-cv" | "submitting" | "done" | "error">("confirm");
  const [applyError, setApplyError] = useState("");
  const [bookmarkAnimating, setBookmarkAnimating] = useState(false);

  const saved = isSaved(job.id);

  const handleBookmark = async () => {
    if (!isAuthenticated) {
      setShowBookmarkLoginPrompt(true);
      return;
    }

    setBookmarkAnimating(true);
    await toggleSave(job.id);
    setTimeout(() => setBookmarkAnimating(false), 400);
  };

  const handleViewMore = () => {
    if (!isAuthenticated) {
      setShowLoginPrompt(true);
    } else {
      setExpanded(!expanded);
    }
  };

  const handleApplyClick = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setShowLoginPrompt(true);
      return;
    }

    if (user.status === "banned") {
      setShowBannedAlert(true);
      return;
    }

    setApplyState("confirm");
    setApplyError("");

    const { data: cv } = await supabase
      .from("vietnamcv_cvs")
      .select("id")
      .eq("user_id", user.id)
      .maybeSingle();

    if (!cv) {
      setApplyState("no-cv");
    }

    setShowApplyModal(true);
  }, [isAuthenticated, user]);

  const handleConfirmApply = async () => {
    if (!user) return;
    setApplyState("submitting");
    setApplyError("");

    try {
      const { data: cv } = await supabase
        .from("vietnamcv_cvs")
        .select("id")
        .eq("user_id", user.id)
        .maybeSingle();

      const { error } = await supabase.from("vietnamcv_applications").insert({
        user_id: user.id,
        job_id: job.id,
        cv_id: cv?.id || null,
        status: "pending",
      });

      if (error) {
        if (error.message.includes("duplicate") || error.code === "23505") {
          setApplyError("Bạn đã ứng tuyển công việc này rồi.");
          setApplyState("error");
        } else {
          setApplyError(error.message);
          setApplyState("error");
        }
      } else {
        setApplyState("done");
      }
    } catch {
      setApplyError("Có lỗi xảy ra. Vui lòng thử lại.");
      setApplyState("error");
    }
  };

  const closeApplyModal = () => {
    setShowApplyModal(false);
    setApplyState("confirm");
    setApplyError("");
  };

  const closeBannedAlert = useCallback(() => {
    setShowBannedAlert(false);
  }, []);

  const handleContactClick = () => {
    setShowContactModal(true);
  };

  const closeContactModal = useCallback(() => {
    setShowContactModal(false);
  }, []);

  const isAdminPosted = job.postedBy === "admin";

  const typeLabels: Record<string, string> = {
    "full-time": "Toàn thời gian",
    "part-time": "Bán thời gian",
    contract: "Hợp đồng",
  };

  const handleCloseLoginPrompt = useCallback(() => {
    setShowLoginPrompt(false);
  }, []);

  const handleCloseBookmarkLoginPrompt = useCallback(() => {
    setShowBookmarkLoginPrompt(false);
  }, []);

  return (
    <>
      <div
        className={`bg-background-50 rounded-xl border-2 border-orange-400/50 overflow-hidden transition-all duration-400 group hover:border-orange-400 hover:shadow-[0_0_18px_rgba(251,146,60,0.12)] ${
          expanded ? "border-orange-400 shadow-[0_0_18px_rgba(251,146,60,0.1)]" : ""
        }`}
      >
        <div className="p-5 md:p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 md:w-12 md:h-12 rounded-lg bg-primary-100/70 flex items-center justify-center flex-shrink-0">
                <i className="ri-building-line text-primary-600 text-lg md:text-xl"></i>
              </div>
              <div className="min-w-0">
                <h3 className="font-heading font-semibold text-base text-foreground-900 truncate">
                  {job.title}
                </h3>
                <Link
                  to={`/nha-tuyen-dung/${slugify(job.company)}`}
                  className="text-sm text-foreground-600 truncate hover:text-primary-600 transition-colors no-underline cursor-pointer"
                >
                  {job.company}
                </Link>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={handleBookmark}
                className={`w-9 h-9 flex items-center justify-center rounded-full transition-all duration-200 cursor-pointer ${
                  saved
                    ? "bg-accent-100/80 text-accent-600 hover:bg-accent-200/80"
                    : "text-foreground-400 hover:text-accent-500 hover:bg-background-100"
                } ${bookmarkAnimating ? "scale-125" : ""}`}
                title={saved ? "Bỏ lưu việc làm này" : "Lưu việc làm này"}
              >
                <i className={`${saved ? "ri-bookmark-fill" : "ri-bookmark-line"} text-lg`}></i>
              </button>
              {job.isHot && (
                <span className="px-2.5 py-1 bg-primary-500 text-background-50 text-xs font-semibold rounded-full whitespace-nowrap">
                  HOT
                </span>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className="inline-flex items-center gap-1 text-xs text-foreground-600 bg-secondary-100/70 px-2.5 py-1 rounded-full">
              <i className="ri-map-pin-line text-xs"></i>
              {job.location}
            </span>
            <span className="inline-flex items-center gap-1 text-xs text-accent-700 bg-accent-100/70 px-2.5 py-1 rounded-full font-medium">
              <i className="ri-money-dollar-circle-line text-xs"></i>
              {job.salary}
            </span>
            <span className="inline-flex items-center gap-1 text-xs text-foreground-500 bg-background-200/70 px-2.5 py-1 rounded-full">
              <i className="ri-briefcase-4-line text-xs"></i>
              {typeLabels[job.type] || job.type}
            </span>
          </div>

          <p className="text-sm text-foreground-700 leading-relaxed line-clamp-2 mb-4">
            {job.description}
          </p>

          <div className="flex items-center justify-between">
            <span className="text-xs text-foreground-400">
              <i className="ri-time-line mr-1"></i>
              {job.postedAt}
            </span>
            <button
              onClick={handleViewMore}
              className="flex items-center gap-1.5 px-4 py-2 border border-foreground-300 rounded-full text-sm font-medium text-foreground-700 hover:border-primary-400 hover:text-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              <span>Xem thêm</span>
              <i
                className={`ri-arrow-down-s-line text-base transition-transform duration-300 ${
                  expanded ? "rotate-180" : ""
                }`}
              ></i>
            </button>
          </div>
        </div>

        <div
          className={`transition-all duration-400 ease-in-out overflow-hidden ${
            expanded ? "max-h-[700px] opacity-100" : "max-h-0 opacity-0"
          }`}
        >
          <div className="px-5 md:px-6 pb-5 md:pb-6 border-t border-background-200/70 pt-4">
            <div className="mb-4">
              <h4 className="font-heading font-semibold text-sm text-foreground-900 mb-2">
                <i className="ri-file-text-line mr-2 text-primary-500"></i>
                Mô tả chi tiết
              </h4>
              <p className="text-sm text-foreground-700 leading-relaxed">
                {job.detail}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <h4 className="font-heading font-semibold text-sm text-foreground-900 mb-2">
                  <i className="ri-checkbox-circle-line mr-2 text-accent-600"></i>
                  Yêu cầu
                </h4>
                <ul className="space-y-1.5">
                  {job.requirements.map((req, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-2 text-sm text-foreground-700"
                    >
                      <i className="ri-check-line text-accent-500 mt-0.5 flex-shrink-0"></i>
                      <span>{req}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h4 className="font-heading font-semibold text-sm text-foreground-900 mb-2">
                  <i className="ri-gift-line mr-2 text-primary-500"></i>
                  Quyền lợi
                </h4>
                <ul className="space-y-1.5">
                  {job.benefits.map((benefit, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-2 text-sm text-foreground-700"
                    >
                      <i className="ri-star-line text-primary-400 mt-0.5 flex-shrink-0"></i>
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {isAdminPosted ? (
              <button
                onClick={handleContactClick}
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-accent-500 text-background-50 rounded-full text-sm font-semibold hover:bg-accent-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                <span>Liên hệ ngay</span>
                <i className="ri-phone-line text-base"></i>
              </button>
            ) : (
              <button
                onClick={handleApplyClick}
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                <span>Ứng tuyển ngay</span>
                <i className="ri-send-plane-line text-base"></i>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Bookmark Login Prompt */}
      <AnimatedModal isOpen={showBookmarkLoginPrompt} onClose={handleCloseBookmarkLoginPrompt} variant="fade-up">
        <div className="bg-background-50 rounded-2xl w-[90vw] max-w-md overflow-hidden shadow-2xl">
          <div className="h-1.5 bg-gradient-to-r from-orange-400 via-orange-500 to-amber-500"></div>
          <div className="p-8 text-center">
            <div className="w-20 h-20 mx-auto mb-5 rounded-full bg-gradient-to-br from-orange-50 to-amber-100 border-2 border-orange-200 flex items-center justify-center">
              <i className="ri-bookmark-line text-3xl text-orange-500"></i>
            </div>
            <h3 className="font-heading font-bold text-2xl text-foreground-950 mb-2">
              Bạn chưa đăng nhập
            </h3>
            <p className="text-sm text-foreground-600 mb-2 leading-relaxed">
              Đăng nhập để lưu việc làm và xem lại sau
            </p>
            <p className="text-xs text-foreground-400 mb-7">
              Chưa có tài khoản? Bạn sẽ được chuyển đến trang đăng ký
            </p>
            <div className="flex flex-col gap-3">
              <Link
                to="/dang-nhap"
                className="w-full px-6 py-3.5 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-full text-base font-bold hover:from-orange-600 hover:to-amber-600 transition-all duration-200 no-underline cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 shadow-[0_4px_20px_rgba(251,146,60,0.35)] hover:shadow-[0_6px_25px_rgba(251,146,60,0.45)]"
                onClick={() => setShowBookmarkLoginPrompt(false)}
              >
                <i className="ri-login-box-line text-lg"></i>
                Đăng nhập / Đăng ký ngay
              </Link>
              <button
                onClick={() => setShowBookmarkLoginPrompt(false)}
                className="w-full py-3 text-sm font-medium text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer"
              >
                Để sau
              </button>
            </div>
          </div>
        </div>
      </AnimatedModal>

      {/* Login Prompt - Animated popup */}
      <AnimatedModal isOpen={showLoginPrompt} onClose={handleCloseLoginPrompt} variant="fade-up">
        <div className="bg-background-50 rounded-2xl w-[90vw] max-w-md overflow-hidden shadow-2xl">
          {/* Top accent bar */}
          <div className="h-1.5 bg-gradient-to-r from-orange-400 via-orange-500 to-amber-500"></div>
          
          <div className="p-8 text-center">
            {/* Icon circle */}
            <div className="w-20 h-20 mx-auto mb-5 rounded-full bg-gradient-to-br from-orange-50 to-amber-100 border-2 border-orange-200 flex items-center justify-center">
              <i className="ri-lock-unlock-line text-3xl text-orange-500"></i>
            </div>
            
            <h3 className="font-heading font-bold text-2xl text-foreground-950 mb-2">
              Bạn chưa đăng nhập
            </h3>
            <p className="text-sm text-foreground-600 mb-2 leading-relaxed">
              Đăng nhập để xem chi tiết công việc và ứng tuyển ngay
            </p>
            <p className="text-xs text-foreground-400 mb-7">
              Chưa có tài khoản? Bạn sẽ được chuyển đến trang đăng ký
            </p>
            
            <div className="flex flex-col gap-3">
              <Link
                to="/dang-nhap"
                className="w-full px-6 py-3.5 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-full text-base font-bold hover:from-orange-600 hover:to-amber-600 transition-all duration-200 no-underline cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 shadow-[0_4px_20px_rgba(251,146,60,0.35)] hover:shadow-[0_6px_25px_rgba(251,146,60,0.45)]"
                onClick={() => setShowLoginPrompt(false)}
              >
                <i className="ri-login-box-line text-lg"></i>
                Đăng nhập / Đăng ký ngay
              </Link>
              <button
                onClick={() => setShowLoginPrompt(false)}
                className="w-full py-3 text-sm font-medium text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer"
              >
                Để sau, xem tiếp
              </button>
            </div>
          </div>
        </div>
      </AnimatedModal>

      {/* Banned User Alert Modal */}
      <AnimatedModal isOpen={showBannedAlert} onClose={closeBannedAlert} variant="fade-up">
        <div className="bg-background-50 rounded-2xl w-[90vw] max-w-md overflow-hidden shadow-2xl">
          <div className="h-1.5 bg-gradient-to-r from-red-400 via-red-500 to-red-600"></div>
          
          <div className="p-8 text-center">
            <div className="w-20 h-20 mx-auto mb-5 rounded-full bg-gradient-to-br from-red-50 to-red-100 border-2 border-red-200 flex items-center justify-center">
              <i className="ri-forbid-2-line text-3xl text-red-500"></i>
            </div>
            
            <h3 className="font-heading font-bold text-2xl text-foreground-950 mb-2">
              Tài khoản bị khóa
            </h3>
            <p className="text-sm text-foreground-600 mb-2 leading-relaxed">
              Tài khoản của bạn đã bị quản trị viên khóa hoạt động. Bạn không thể ứng tuyển công việc trong thời gian này.
            </p>
            <p className="text-xs text-foreground-400 mb-7">
              Vui lòng liên hệ quản trị viên nếu bạn cho rằng đây là nhầm lẫn.
            </p>
            
            <button
              onClick={closeBannedAlert}
              className="w-full px-6 py-3.5 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-full text-base font-bold hover:from-red-600 hover:to-red-700 transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
            >
              <i className="ri-close-line text-lg"></i>
              Đã hiểu
            </button>
          </div>
        </div>
      </AnimatedModal>

      {/* Apply Modal - Animated popup */}
      <AnimatedModal isOpen={showApplyModal} onClose={closeApplyModal} variant="scale">
        <div className="bg-background-50 rounded-2xl p-8 w-[90vw] max-w-md shadow-2xl">
          {applyState === "confirm" && (
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary-100 flex items-center justify-center">
                <i className="ri-send-plane-line text-2xl text-primary-500"></i>
              </div>
              <h3 className="font-heading font-bold text-xl text-foreground-950 mb-2">
                Ứng tuyển {job.title}
              </h3>
              <p className="text-sm text-foreground-600 mb-2">
                <strong>{job.company}</strong> - {job.location}
              </p>
              <p className="text-sm text-foreground-500 mb-6">
                CV của bạn sẽ được gửi đến nhà tuyển dụng. Bạn có thể theo dõi trạng thái ứng tuyển trong Tài khoản của tôi.
              </p>
              <div className="flex flex-col gap-3">
                <button
                  onClick={handleConfirmApply}
                  className="w-full px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Xác nhận ứng tuyển
                </button>
                <button
                  onClick={closeApplyModal}
                  className="w-full px-6 py-3 border border-background-300 rounded-full text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Để sau
                </button>
              </div>
            </div>
          )}

          {applyState === "no-cv" && (
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-100 flex items-center justify-center">
                <i className="ri-file-warning-line text-2xl text-amber-500"></i>
              </div>
              <h3 className="font-heading font-bold text-xl text-foreground-950 mb-2">
                Bạn chưa có CV
              </h3>
              <p className="text-sm text-foreground-600 mb-6">
                Bạn nên tạo CV trước khi ứng tuyển để nhà tuyển dụng có thể xem hồ sơ của bạn. Bạn vẫn có thể ứng tuyển mà không cần CV.
              </p>
              <div className="flex flex-col gap-3">
                <Link
                  to="/tai-khoan?tab=cv"
                  className="w-full px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors no-underline cursor-pointer whitespace-nowrap flex items-center justify-center"
                  onClick={closeApplyModal}
                >
                  <i className="ri-file-text-line mr-2"></i>
                  Tạo CV ngay
                </Link>
                <button
                  onClick={handleConfirmApply}
                  className="w-full px-6 py-3 border border-background-300 rounded-full text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Ứng tuyển không cần CV
                </button>
                <button
                  onClick={closeApplyModal}
                  className="w-full py-2 text-sm text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer"
                >
                  Để sau
                </button>
              </div>
            </div>
          )}

          {applyState === "submitting" && (
            <div className="text-center py-4">
              <div className="w-12 h-12 mx-auto mb-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
              <p className="text-sm text-foreground-600">Đang gửi hồ sơ ứng tuyển...</p>
            </div>
          )}

          {applyState === "done" && (
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                <i className="ri-check-line text-2xl text-green-500"></i>
              </div>
              <h3 className="font-heading font-bold text-xl text-foreground-950 mb-2">
                Ứng tuyển thành công!
              </h3>
              <p className="text-sm text-foreground-600 mb-6">
                Hồ sơ của bạn đã được gửi đến <strong>{job.company}</strong>. Theo dõi trạng thái trong Tài khoản của tôi.
              </p>
              <div className="flex flex-col gap-3">
                <Link
                  to="/tai-khoan?tab=applied"
                  className="w-full px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors no-underline cursor-pointer whitespace-nowrap flex items-center justify-center"
                  onClick={closeApplyModal}
                >
                  <i className="ri-file-list-3-line mr-2"></i>
                  Xem việc đã ứng tuyển
                </Link>
                <button
                  onClick={closeApplyModal}
                  className="w-full py-2 text-sm text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer"
                >
                  Đóng
                </button>
              </div>
            </div>
          )}

          {applyState === "error" && (
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
                <i className="ri-error-warning-line text-2xl text-red-500"></i>
              </div>
              <h3 className="font-heading font-bold text-xl text-foreground-950 mb-2">
                {applyError.includes("đã ứng tuyển") ? "Đã ứng tuyển" : "Có lỗi xảy ra"}
              </h3>
              <p className="text-sm text-foreground-600 mb-6">{applyError}</p>
              <button
                onClick={closeApplyModal}
                className="w-full px-6 py-3 border border-background-300 rounded-full text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                Đóng
              </button>
            </div>
          )}
        </div>
      </AnimatedModal>

      {/* Contact Employer Modal - for admin-posted jobs */}
      <AnimatedModal isOpen={showContactModal} onClose={closeContactModal} variant="scale">
        <div className="bg-background-50 rounded-2xl p-8 w-[90vw] max-w-md shadow-2xl">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-accent-100 flex items-center justify-center">
              <i className="ri-phone-line text-2xl text-accent-600"></i>
            </div>
            <h3 className="font-heading font-bold text-xl text-foreground-950 mb-1">
              Liên hệ nhà tuyển dụng
            </h3>
            <p className="text-sm text-foreground-500 mb-6">
              <strong>{job.company}</strong> - {job.title}
            </p>

            <div className="space-y-4 mb-6">
              {job.employerPhone && (
                <div className="flex items-center gap-3 bg-background-100 rounded-xl p-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 flex-shrink-0">
                    <i className="ri-smartphone-line text-lg"></i>
                  </div>
                  <div className="text-left flex-1 min-w-0">
                    <p className="text-xs text-foreground-500 mb-0.5">Số điện thoại</p>
                    <p className="text-sm font-semibold text-foreground-900 truncate">{job.employerPhone}</p>
                  </div>
                </div>
              )}
              {job.employerEmail && (
                <div className="flex items-center gap-3 bg-background-100 rounded-xl p-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <div className="text-left flex-1 min-w-0">
                    <p className="text-xs text-foreground-500 mb-0.5">Email</p>
                    <p className="text-sm font-semibold text-foreground-900 truncate">{job.employerEmail}</p>
                  </div>
                </div>
              )}
              {!job.employerPhone && !job.employerEmail && (
                <div className="text-center py-3">
                  <p className="text-sm text-foreground-400">Chưa có thông tin liên hệ cho tin tuyển dụng này.</p>
                </div>
              )}
            </div>

            <div className="flex flex-col gap-3">
              {job.employerPhone && (
                <a
                  href={`tel:${job.employerPhone}`}
                  className="w-full px-6 py-3 bg-accent-500 text-background-50 rounded-full text-sm font-semibold hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap no-underline flex items-center justify-center gap-2"
                >
                  <i className="ri-phone-fill text-base"></i>
                  Gọi ngay
                </a>
              )}
              {job.employerEmail && (
                <a
                  href={`mailto:${job.employerEmail}?subject=Ứng tuyển ${encodeURIComponent(job.title)} - ${encodeURIComponent(job.company)}`}
                  className="w-full px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap no-underline flex items-center justify-center gap-2"
                >
                  <i className="ri-mail-send-line text-base"></i>
                  Gửi email
                </a>
              )}
              <button
                onClick={closeContactModal}
                className="w-full px-6 py-3 border border-background-300 rounded-full text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                Đóng
              </button>
            </div>
          </div>
        </div>
      </AnimatedModal>
    </>
  );
}